"use client";

import { Avatar } from "@base-ui/react/avatar";
import { Collapsible } from "@base-ui/react/collapsible";
import { Dialog } from "@base-ui/react/dialog";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import logo2 from "#/assets/bebek-com-logo.svg";
import logo from "#/assets/ebebek.svg";
import type { PlainSession } from "@/actions/auth.action";
import ChevronDown from "@/components/icons/chevron-down";
import HamburgerIcon from "@/components/icons/hamburger";
import ProfileIcon from "@/components/icons/profile";
import type { Category } from "@/types/category";
import { HeaderLinks, tools } from "../const";

const SECONDARY_HREFS = ["/quiz", "/akademi", "/hakkimizda", "/iletisim"];
const secondaryLinks = HeaderLinks.filter((l) =>
  SECONDARY_HREFS.includes(l.href),
);

interface MobileMenuDialogProps {
  session: PlainSession | null;
  articleCategories: Category[];
  tvCategories: Category[];
  guideCategories: Category[];
}

export default function MobileMenuDialog({
  session,
  articleCategories,
  tvCategories,
  guideCategories,
}: MobileMenuDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <>
      <button
        aria-label="Menüyü Aç"
        className="border block md:hidden border-brand-blue-200 rounded-full text-brand-blue-600 p-2 font-bold hover:bg-brand-blue-200 transition-colors duration-200 cursor-pointer"
        onClick={() => setIsOpen(true)}
        type="button"
      >
        <HamburgerIcon className="size-5 stroke-brand-blue-600" />
      </button>

      <Dialog.Root onOpenChange={setIsOpen} open={isOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/20 transition-all duration-200 data-starting-style:opacity-0 data-ending-style:opacity-0" />
          <Dialog.Popup className="fixed inset-0 z-50 flex flex-col bg-white outline-none w-full h-full overflow-y-auto transition-all duration-200 data-starting-style:opacity-0 data-starting-style:translate-y-4 data-ending-style:opacity-0 data-ending-style:translate-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between px-4 h-16 shrink-0">
              <Link href="/" onClick={() => setIsOpen(false)}>
                <Image
                  alt="bebek.com Logo"
                  className="h-10 w-auto"
                  height={40}
                  src={logo2}
                  width={149}
                />
              </Link>
              <Dialog.Close
                aria-label="Menüyü Kapat"
                className="border border-brand-blue-200 rounded-full text-brand-blue-600 p-2 hover:bg-brand-blue-200 transition-colors duration-200 cursor-pointer flex items-center justify-center"
              >
                <svg
                  className="size-5"
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <title>Close</title>
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </Dialog.Close>
            </div>

            <div className="border-b border-neutral-100" />

            {/* Profile section */}
            <div className="px-4 py-4">
              {session?.isLoggedIn ? (
                <Link
                  className="flex items-center gap-3 w-full"
                  href="/profile"
                  onClick={() => setIsOpen(false)}
                >
                  <Avatar.Root className="inline-flex size-12 items-center justify-center overflow-hidden rounded-full bg-gray-100 align-middle text-base text-gray-900 select-none shrink-0">
                    <Avatar.Image
                      className="size-full object-cover"
                      height="48"
                      src={session.user?.imageUrl}
                      width="48"
                    />
                    <Avatar.Fallback
                      className="flex size-full items-center justify-center text-base"
                      delay={600}
                    >
                      {session.user?.name[0]}
                    </Avatar.Fallback>
                  </Avatar.Root>
                  <span className="font-bold text-base">Profilim</span>
                  <ChevronDown className="stroke-neutral-400 fill-none ml-auto -rotate-90 size-4" />
                </Link>
              ) : (
                <Link
                  className="border flex w-full items-center justify-center gap-2 border-brand-blue-200 text-sm leading-5 rounded-full text-brand-blue-600 px-4 py-3 font-bold hover:bg-brand-blue-200 transition-colors duration-200"
                  href="/auth/login"
                  onClick={() => setIsOpen(false)}
                >
                  <ProfileIcon className="size-5" /> Üye Ol / Giriş Yap
                </Link>
              )}
            </div>

            <div className="border-b border-neutral-100" />

            {/* Primary menu */}
            <nav className="flex flex-col">
              <CollapsibleMenuItem label="Makaleler">
                {articleCategories.map((cat) => (
                  <SubLink
                    href={`/makale/${cat.slug}`}
                    iconUrl={cat.iconUrl}
                    key={cat.id}
                    label={cat.name}
                    onClick={() => setIsOpen(false)}
                  />
                ))}
                <SubLink
                  href="/makale"
                  label="Tüm Makaleler"
                  onClick={() => setIsOpen(false)}
                />
              </CollapsibleMenuItem>

              <CollapsibleMenuItem label="bebek.com TV">
                {tvCategories.map((cat) => (
                  <SubLink
                    href={`/tv/${cat.slug}`}
                    iconUrl={cat.iconUrl}
                    key={cat.id}
                    label={cat.name}
                    onClick={() => setIsOpen(false)}
                  />
                ))}
                <SubLink
                  href="/tv"
                  label="Tümü"
                  onClick={() => setIsOpen(false)}
                />
              </CollapsibleMenuItem>

              <CollapsibleMenuItem label="Alışveriş Rehberi">
                {guideCategories.map((cat) => (
                  <SubLink
                    href={`/rehber/${cat.slug}`}
                    iconUrl={cat.iconUrl}
                    key={cat.id}
                    label={cat.name}
                    onClick={() => setIsOpen(false)}
                  />
                ))}
                <SubLink
                  href="/rehber"
                  label="Tüm Rehberler"
                  onClick={() => setIsOpen(false)}
                />
              </CollapsibleMenuItem>

              <DirectMenuItem
                href="/forum"
                label="Forum"
                onClick={() => setIsOpen(false)}
              />
              <DirectMenuItem
                href="/danismanliklar"
                label="Danışmanlıklar"
                onClick={() => setIsOpen(false)}
              />

              <CollapsibleMenuItem label="Araçlar">
                {tools.map((tool) => (
                  <SubLink
                    href={tool.href}
                    key={tool.id}
                    label={tool.title}
                    onClick={() => setIsOpen(false)}
                  />
                ))}
              </CollapsibleMenuItem>
            </nav>

            {/* Secondary section */}
            <div className="bg-brand-blue-50 flex flex-col px-4 py-4 mt-auto">
              {secondaryLinks.map((link) => (
                <Link
                  className="text-sm text-neutral-600 py-2.5 font-medium hover:text-brand-blue-600 transition-colors"
                  href={link.href}
                  key={link.href}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4">
                <Image alt="ebebek Logo" height={20} src={logo} width={77} />
              </div>
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function CollapsibleMenuItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Collapsible.Root>
      <Collapsible.Trigger className="flex items-center justify-between w-full px-4 py-4 border-b border-neutral-100 font-bold text-base text-left cursor-pointer hover:bg-neutral-50 transition-colors group">
        {label}
        <ChevronDown className="stroke-neutral-400 fill-none size-4 transition-transform duration-200 group-data-[open]:rotate-180" />
      </Collapsible.Trigger>
      <Collapsible.Panel className="overflow-hidden transition-all duration-200 data-[open]:animate-none border-b border-neutral-100 bg-neutral-50">
        {children}
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

function DirectMenuItem({
  label,
  href,
  onClick,
}: {
  label: string;
  href: string;
  onClick: () => void;
}) {
  return (
    <Link
      className="flex items-center justify-between px-4 py-4 border-b border-neutral-100 font-bold text-base hover:bg-neutral-50 transition-colors"
      href={href}
      onClick={onClick}
    >
      {label}
      <ChevronDown className="stroke-neutral-400 fill-none size-4 -rotate-90" />
    </Link>
  );
}

function SubLink({
  label,
  href,
  iconUrl,
  onClick,
}: {
  label: string;
  href: string;
  iconUrl?: string;
  onClick: () => void;
}) {
  return (
    <Link
      className="flex items-center gap-3 px-6 py-3 text-sm text-neutral-600 font-medium hover:text-brand-blue-600 hover:bg-neutral-100 transition-colors border-b border-neutral-100 last:border-b-0"
      href={href}
      onClick={onClick}
    >
      {iconUrl && (
        <Image
          alt={label}
          className="size-8 rounded object-cover shrink-0"
          height={32}
          src={iconUrl}
          width={32}
        />
      )}
      {label}
    </Link>
  );
}
